import { validateArray, validateShapesMatch, validateRange } from '../utils/validation.js';
import { shuffle as shuffleArray } from '../utils/random.js';

export function trainTestSplit(X, y, options = {}) {
  validateArray(X, 'X');
  
  if (y !== undefined) {
    validateArray(y, 'y');
    validateShapesMatch(X, y);
  }
  
  const testSize = options.testSize !== undefined ? options.testSize : 0.25;
  const trainSize = options.trainSize;
  const shuffle = options.shuffle !== undefined ? options.shuffle : true;
  const randomState = options.randomState;
  
  validateRange(testSize, 0, 1, 'testSize');
  
  const n = X.length;
  let nTest;
  
  if (trainSize !== undefined) {
    validateRange(trainSize, 0, 1, 'trainSize');
    nTest = Math.floor(n * (1 - trainSize));
  } else {
    nTest = Math.floor(n * testSize);
  }
  
  const nTrain = n - nTest;
  
  let indices = Array.from({ length: n }, (_, i) => i);
  
  if (shuffle) {
    indices = shuffleArray(indices);
  }
  
  const trainIndices = indices.slice(0, nTrain);
  const testIndices = indices.slice(nTrain);
  
  const XTrain = trainIndices.map(i => X[i]);
  const XTest = testIndices.map(i => X[i]);
  
  if (y !== undefined) {
    const yTrain = trainIndices.map(i => y[i]);
    const yTest = testIndices.map(i => y[i]);
    return { XTrain, XTest, yTrain, yTest };
  }
  
  return { XTrain, XTest };
}

export function kFoldSplit(n, options = {}) {
  const nSplits = options.nSplits || 5;
  const shuffle = options.shuffle !== undefined ? options.shuffle : false;
  
  if (nSplits <= 1 || nSplits > n) {
    throw new Error(`nSplits must be between 2 and ${n}, got ${nSplits}`);
  }
  
  let indices = Array.from({ length: n }, (_, i) => i);
  
  if (shuffle) {
    indices = shuffleArray(indices);
  }
  
  const foldSize = Math.floor(n / nSplits);
  const folds = [];
  
  for (let i = 0; i < nSplits; i++) {
    const start = i * foldSize;
    const end = i === nSplits - 1 ? n : start + foldSize;
    
    const testIndices = indices.slice(start, end);
    const trainIndices = [...indices.slice(0, start), ...indices.slice(end)];
    
    folds.push({ trainIndices, testIndices });
  }
  
  return folds;
}

export function crossValidate(model, X, y, options = {}) {
  validateArray(X, 'X');
  validateArray(y, 'y');
  validateShapesMatch(X, y);
  
  const cv = options.cv || 5;
  const scoring = options.scoring || 'accuracy';
  
  const folds = kFoldSplit(X.length, { nSplits: cv, shuffle: true });
  const scores = [];
  
  for (const fold of folds) {
    const XTrain = fold.trainIndices.map(i => X[i]);
    const yTrain = fold.trainIndices.map(i => y[i]);
    const XTest = fold.testIndices.map(i => X[i]);
    const yTest = fold.testIndices.map(i => y[i]);
    
    const clonedModel = Object.create(Object.getPrototypeOf(model));
    Object.assign(clonedModel, JSON.parse(JSON.stringify(model)));
    clonedModel.isFitted = false;
    
    clonedModel.fit(XTrain, yTrain);
    
    let score;
    if (typeof scoring === 'function') {
      score = scoring(clonedModel, XTest, yTest);
    } else if (scoring === 'accuracy' || scoring === 'score') {
      score = clonedModel.score(XTest, yTest);
    } else {
      score = clonedModel.score(XTest, yTest);
    }
    
    scores.push(score);
  }
  
  const meanScore = scores.reduce((a, b) => a + b, 0) / scores.length;
  const variance = scores.reduce((sum, s) => sum + Math.pow(s - meanScore, 2), 0) / scores.length;
  const stdScore = Math.sqrt(variance);
  
  return {
    scores,
    meanScore,
    stdScore,
    cv
  };
}
